/**
 * Branding por hostname (white-label).
 *
 * El mismo build se sirve desde admin.gopersonal.ai y desde dominios
 * white-label (hoy: personalization.innew.ai, vía reverse proxy). config.json
 * lo genera el container por ambiente, no por dominio, así que la única señal
 * para distinguirlos en runtime es window.location.hostname.
 *
 * Default = branding gopersonal (localhost, admin.gopersonal.ai, staging...).
 * Solo los hosts que matchean WHITE_LABEL_HOSTS esconden logo/marca.
 */
import appConfig from "@/app.config";

const WHITE_LABEL_HOSTS = [/\.innew\.ai$/i];

const hostname =
  typeof window !== "undefined" && window.location ? window.location.hostname : "";

export const isWhiteLabel = WHITE_LABEL_HOSTS.some((re) => re.test(hostname));

export const brand = isWhiteLabel
  ? {
      // Nombre neutro: va al <title> de la pestaña ("Login | Personalization").
      title: "Personalization",
      // Logo principal de innew (violeta), en el bucket público de admin-apis:
      // pantallas de auth (login y demás) y arriba a la izquierda del admin.
      showLogo: true,
      logo: "https://public-assets.gopersonal.ai/uploads/2026/10/b4461cd7-6f2b-4b0a-b598-e9a1026bb0d2.svg",
      navLogo: "https://public-assets.gopersonal.ai/uploads/2026/10/b4461cd7-6f2b-4b0a-b598-e9a1026bb0d2.svg",
      navLogoStyle: "",
      // Isologo de innew (el original viene en blanco; este es el violeta del logo, recortado): menú colapsado y favicon.
      navIcon: "https://public-assets.gopersonal.ai/uploads/2026/10/ec8224fe-df88-4d96-82ae-933164d34446.png",
      // Sin © en el footer de las pantallas de auth.
      copyright: "",
    }
  : {
      title: appConfig.title,
      showLogo: true,
      logo: "https://gopersonal.com/newlogo.svg",
      navLogo: "https://gopersonal.com/newlogo.svg",
      navLogoStyle: "",
      navIcon: "",
      copyright: "gopersonal.",
    };

/**
 * Saca la marca gopersonal de un texto visible ("gopersonal Apikey" → "Apikey",
 * "Go Personal Shopper" → "Personal Shopper"). Fuera de white-label lo deja igual.
 */
export function unbrand(text) {
  if (!isWhiteLabel || typeof text !== "string") return text;
  return text
    .replace(/^Go\s+(?=Personal)/, "")
    .replace(/\bgopersonal\b\s*/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

// Ícono de integración con el símbolo de gopersonal → uno neutro (una llave) en white-label.
const BRAND_ICON = /public-assets\.gopersonal\.ai\/uploads\/2026\/07\/5e5fe2bd-fc33-430e-9f0b-3845bf35359d\.svg/;
const NEUTRAL_ICON = "data:image/svg+xml," + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#556ee6" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="15" r="4"/><path d="M10.8 12.2 20 3M17 6l3 3M14 9l2 2"/></svg>'
);

export function unbrandIcon(url) {
  return isWhiteLabel && typeof url === "string" && BRAND_ICON.test(url) ? NEUTRAL_ICON : url;
}

/**
 * URLs públicas de admin-api que se le muestran al cliente (p. ej. el link de
 * conexión de Zernio): en white-label salen por el /api del propio dominio, que
 * el reverse proxy (calvincode-gateway/caddy) manda a admin-api con el mismo path.
 */
const ADMIN_API_ORIGIN = /^https:\/\/admin-api\.goshops\.ai(?=\/api(?:\/|$))/i;

export function unbrandApiUrl(url) {
  return isWhiteLabel && typeof url === "string" ? url.replace(ADMIN_API_ORIGIN, `https://${hostname}`) : url;
}

/**
 * Links a sitios con la marca gopersonal (Academy, docs de la API, novedades):
 * en white-label se ocultan con una regla global (y los botones que los abren por
 * JS llevan la clase `branded-link`), así no hay que acordarse de
 * cada pantalla que los muestra (son decenas) ni de las nuevas.
 */
const BRANDED_LINKS = [
  "academy.gopersonal.ai",
  "docs.goshops.com",
  "gopersonal.canny.io",
];

/**
 * Ajustes en el <head> que el build inyecta con la marca gopersonal (favicon,
 * icono PWA, manifest, título iOS). En white-label el favicon pasa a ser el
 * isologo de la marca y se saca el manifest de gopersonal.
 */
export function applyBrand() {
  if (!isWhiteLabel || typeof document === "undefined") return;

  document.documentElement.classList.add("white-label");
  const style = document.createElement("style");
  style.setAttribute("data-white-label", "");
  style.textContent = BRANDED_LINKS
    .map((host) => `.white-label a[href*="${host}"]`)
    .concat(".white-label .branded-link")
    .join(",\n") + " { display: none !important; }";
  document.head.appendChild(style);

  document
    .querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"], link[rel="mask-icon"]')
    .forEach((el) => el.setAttribute("href", brand.navIcon || "data:,"));

  document.querySelectorAll('link[rel="manifest"]').forEach((el) => el.remove());

  const iosTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (iosTitle) iosTitle.setAttribute("content", brand.title);
}
